import type { LandingContent } from './types';

export const de: LandingContent = {
  locale: 'de',
  seo: {
    title: 'Maxim Domentii | Softwarearchitekt, SaaS- & KI-Automatisierungsberater in der Schweiz',
    description:
      'Senior Software Engineer in der Schweiz für SaaS-Produkte, cloud-native Systeme, Websites, KI-Agenten und moderne digitale Workflows.',
    ogTitle: 'Softwarearchitektur, SaaS-Entwicklung und KI-Automatisierung in der Schweiz',
    ogDescription:
      'Maxim Domentii unterstützt Schweizer Unternehmen bei Architektur, Entwicklung, Cloud-Plattformen, Business-Websites und KI-gestützten Workflows.',
  },
  nav: {
    services: 'Leistungen',
    solutions: 'Pakete',
    process: 'Prozess',
    experience: 'Erfahrung',
    faq: 'FAQ',
    contact: 'Kontakt',
    linkedin: 'LinkedIn',
    menu: 'Navigationsmenü öffnen',
    close: 'Navigationsmenü schliessen',
  },
  hero: {
    eyebrow: 'Unabhängiger Softwareberater in der Region Zürich',
    h1: 'Ich entwerfe und entwickle cloud-native Software, SaaS-Produkte und KI-gestützte Geschäftsprozesse.',
    lead: 'Ich unterstütze Schweizer KMU, Gründerinnen und Gründer sowie Produktteams dabei, Ideen mit Softwarearchitektur, Full-Stack-Entwicklung, DevOps und pragmatischer KI-Automatisierung in Produktion zu bringen.',
    primaryCta: 'Projekt besprechen',
    secondaryCta: 'Leistungen ansehen',
    trustBullets: [
      '10+ Jahre Software Engineering',
      'Senior Engineer in Schweizer Banking-Umgebungen',
      'AWS Certified Cloud Practitioner',
      'Java, Spring Boot, React, Kubernetes, Kafka',
    ],
    visualAlt:
      'Architekturkarte mit verbundenen Modulen für SaaS-Produkt, Cloud-Plattform und KI-Workflow.',
  },
  trustBar: {
    items: [
      { value: '2015', label: 'Start der Software-Engineering-Laufbahn' },
      { value: '10+', label: 'Jahre Erfahrung mit Produktionssystemen' },
      { value: 'AWS', label: 'Certified Cloud Practitioner' },
      { value: 'CH', label: 'In der Region Zürich ansässig' },
    ],
  },
  services: {
    eyebrow: 'Leistungen',
    h2: 'Ein Partner für Strategie, Architektur und Umsetzung.',
    intro:
      'Von der digitalen Basis für ein neues Unternehmen bis zur produktionsreifen SaaS-Plattform verbinde ich Engineering-Disziplin mit Produktdenken und modernen KI-Werkzeugen.',
    items: [
      {
        id: 'launch',
        title: 'Firmengründung & digitale Identität',
        description:
          'Starten oder erneuern Sie Ihr Unternehmen mit einem klaren digitalen Fundament: Logo-Richtung, Visitenkarten, Domain, Business-E-Mail, Website, Kontaktformular und optionalen KI-Assistenz-Workflows.',
        deliverables: [
          'Branding-Grundlagen',
          'Domain und E-Mail',
          'Responsive Website',
          'SEO-Basis',
          'Kontaktprozess',
          'KI-Optionen',
        ],
      },
      {
        id: 'saas',
        title: 'SaaS-Konzeption & Umsetzung',
        description:
          'Ich bringe Produktideen in ein funktionsfähiges SaaS-MVP oder eine produktionsreife Anwendung mit klarer Architektur, wartbarem Code und skalierbarem Lieferplan.',
        deliverables: [
          'Product Discovery',
          'Frontend',
          'Backend',
          'APIs',
          'Authentifizierungsstrategie',
          'Datenbankdesign',
          'Deployment-Pipeline',
        ],
      },
      {
        id: 'architecture',
        title: 'Softwarearchitektur & Modernisierung',
        description:
          'Ich entwerfe robuste Systeme, verbessere bestehende Plattformen und treffe technische Entscheidungen, die Risiken reduzieren und Lieferung beschleunigen.',
        deliverables: [
          'Architektur-Reviews',
          'Systemdesign',
          'Microservices',
          'Integrationsmuster',
          'Performance-Verbesserungen',
          'Technische Roadmaps',
        ],
      },
      {
        id: 'ai',
        title: 'KI-Agenten & Automatisierung',
        description:
          'Ich setze KI dort ein, wo sie messbaren Nutzen schafft: Kundenanfragen, interne Wissensprozesse, Lead-Qualifizierung, Operations-Support und Entwicklerproduktivität.',
        deliverables: [
          'KI-Workflow-Design',
          'Agent-Prototypen',
          'Prompt- und Tool-Design',
          'Integrationen',
          'Human-in-the-Loop-Kontrolle',
          'Sichere Einführung',
        ],
      },
      {
        id: 'cloud',
        title: 'Cloud, DevOps & CI/CD',
        description:
          'Ich deploye und betreibe Anwendungen mit automatisierten Pipelines, containerisierter Infrastruktur und Cloud-Praktiken für Zuverlässigkeit und schnelle Iteration.',
        deliverables: [
          'Docker',
          'Kubernetes',
          'CI/CD',
          'Cloud-Deployments',
          'Observability-Basis',
          'Release-Prozess-Verbesserungen',
        ],
      },
      {
        id: 'consulting',
        title: 'Software- & Digitalberatung',
        description:
          'Erhalten Sie Senior-Engineering-Beratung, bevor Sie Budget in einen Anbieter, eine Plattform, Architektur oder Implementierung investieren.',
        deliverables: [
          'Technische Discovery',
          'Anbieterbewertung',
          'Machbarkeitsprüfung',
          'Umsetzungsplanung',
          'Architekturdokumentation',
        ],
      },
    ],
  },
  packages: {
    h2: 'Wählen Sie den passenden Startpunkt für Ihr Unternehmen.',
    items: [
      {
        title: 'Launch-Paket',
        audience:
          'Für neue Unternehmen, Selbstständige und kleine Betriebe, die eine professionelle digitale Basis brauchen.',
        includes: [
          'Branding-Basis',
          'Domain',
          'Business-E-Mail',
          'Landing Page',
          'Rechtliche Seitenstruktur',
          'Kontaktformular',
          'SEO-Setup',
          'Deployment',
        ],
      },
      {
        title: 'SaaS-MVP-Paket',
        audience:
          'Für Gründerinnen, Gründer und Teams, die ein funktionierendes Produkt brauchen, nicht nur einen Prototyp.',
        includes: [
          'Discovery-Workshop',
          'Architektur',
          'UI',
          'API',
          'Datenbank',
          'Authentifizierungsplan',
          'Deployment',
          'Iterations-Roadmap',
        ],
      },
      {
        title: 'KI-Workflow-Paket',
        audience:
          'Für Unternehmen, die wiederkehrende Arbeit automatisieren möchten, ohne die Kontrolle über Qualität zu verlieren.',
        includes: [
          'Workflow-Mapping',
          'Automatisierungsdesign',
          'KI-Agent-Prototyp',
          'Integrationen',
          'Schutzmechanismen',
          'Schulung',
          'Übergabe',
        ],
      },
    ],
  },
  process: {
    h2: 'Ein Lieferprozess mit Klarheit.',
    steps: [
      {
        title: 'Verstehen',
        text: 'Wir klären Ziel, Nutzer, Rahmenbedingungen, Risiken und Erfolgskriterien, bevor Code geschrieben wird.',
      },
      {
        title: 'Entwerfen',
        text: 'Ich definiere User Flow, Architektur, Datenmodell und Lieferplan, damit technische Entscheidungen abgestimmt bleiben.',
      },
      {
        title: 'Umsetzen',
        text: 'Ich entwickle die Lösung mit sauberem Code, modernen Werkzeugen, automatisierten Checks und regelmässigen Demos.',
      },
      {
        title: 'Starten',
        text: 'Wir deployen, validieren, beobachten und bereiten die nächste Iteration mit praktischer Dokumentation vor.',
      },
    ],
  },
  experience: {
    h2: 'Senior-Engineering-Erfahrung in anspruchsvollen Umgebungen.',
    body: 'Seit 2015 arbeite ich in Software Engineering, Banking, Cloud-Plattformen, Webanwendungen und Digitalisierungsprojekten. Meine Erfahrung umfasst Senior-Rollen in Zürich, cloudbasierte Echtzeit-Datenanwendungen, Investment-Content-Plattformen, Reporting-Systeme, SSL-Zertifikatsplattformen und Internet-Banking-Lösungen.',
    highlights: [
      'Entwicklungsteams bei einer cloudbasierten Echtzeit-Cashflow-Anwendung geführt',
      'Algorithmen und skalierbare Backend-Services entworfen',
      'CI/CD-Pipelines aufgebaut und verbessert',
      'Mit Java, Spring Boot, React, Kafka, Kubernetes, Docker und SQL gearbeitet',
      'Entwickler gecoacht, Produktions-Releases begleitet und agile Prozesse verbessert',
    ],
    timelineTitle: 'Beruflicher Werdegang',
    timeline: [
      {
        period: 'Feb. 2022 - heute',
        role: 'Senior Software Engineer',
        organization: 'UBS AG, Zürich',
        context: 'Senior Engineering in bedeutenden Schweizer Banking-Umgebungen.',
      },
      {
        period: 'Mai 2021 - Jan. 2022',
        role: 'Senior Software Engineer',
        organization: 'Cognizant Technology Solutions AG für Credit Suisse',
        context: 'Umsetzung für Banking-Plattformen und digitale Workflows.',
      },
      {
        period: 'Dez. 2019 - Apr. 2021',
        role: 'Senior Software Engineer',
        organization: 'EPAM Systems Switzerland für UBS',
        context: 'Cloudbasierte Echtzeit-Datenanwendungen und Plattformumsetzung.',
      },
      {
        period: 'Aug. 2018 - Nov. 2019',
        role: 'Senior Software Developer',
        organization: 'RINF TECH für 1&1 IONOS',
        context: 'SSL-Zertifikatsplattformen und Backend-Engineering.',
      },
      {
        period: 'Feb. 2016 - Juli 2018',
        role: 'Software Developer',
        organization: 'UniCredit Services',
        context: 'Internet-Banking-Lösungen und Reporting-Systeme.',
      },
      {
        period: 'Sept. 2012 - Juli 2016',
        role: 'Computer Science',
        organization: 'Polytechnic University of Bucharest',
        context: 'Informatikstudium mit Engineering-Grundlagen.',
      },
    ],
  },
  ai: {
    h2: 'KI-bewusstes Engineering ohne Hype.',
    body: 'Ich nutze moderne KI-Coding-Copiloten und strukturierte Engineering-Workflows, um Lieferung zu beschleunigen, ohne Architektur, Codequalität und Sicherheit aus der Hand zu geben. Zudem habe ich zur Schulung und Bewertung führender KI-Modelle beigetragen und dadurch ein praktisches Verständnis dafür entwickelt, wo KI hilft und wo Schutzmechanismen notwendig sind.',
    bullets: [
      'KI-unterstützte Umsetzung mit Senior-Engineering-Review',
      'Automatisierungsideen auf Basis echter Geschäftsprozesse',
      'Human-in-the-Loop-Design für kritische Entscheidungen',
      'Klare Grenzen bei Datenschutz, Qualität und Wartbarkeit',
    ],
  },
  testimonials: {
    h2: 'Was Kolleginnen und Kollegen sagen',
    items: [
      {
        quote: 'Max is a bright and result-driven guy',
        author: 'Mihai Lacatusu',
        role: 'Head of Domains & SSL Engineering at 1&1 IONOS',
      },
      {
        quote: 'Max demonstrated strong conceptual skills...',
        author: 'Martynas Gaurilcikas',
        role: 'Business Analyst',
      },
      {
        quote: 'His mind is about clarity and this could also be seen in his code',
        author: 'Lucian Ilie',
        role: 'Software Engineer',
      },
    ],
    cta: 'LinkedIn-Empfehlungen ansehen',
  },
  faq: {
    h2: 'Häufige Fragen',
    items: [
      {
        question: 'Können Sie Strategie und Umsetzung übernehmen?',
        answer:
          'Ja. Ich kann mit Discovery und Architektur starten und anschliessend Full-Stack-Umsetzung, Deployment und Iteration übernehmen.',
      },
      {
        question: 'Arbeiten Sie auch mit kleinen Unternehmen?',
        answer:
          'Ja. Ich unterstütze Schweizer KMU, Gründerinnen, Gründer, Selbstständige und Produktteams, die Senior-Engineering-Kompetenz brauchen, ohne sofort ein internes Team aufzubauen.',
      },
      {
        question: 'Können Sie eine komplette Website und digitale Basis aufbauen?',
        answer:
          'Ja. Für neue Unternehmen oder Rebrandings kann ich Domain, Business-E-Mail, Landing Page, Kontaktprozess, SEO-Basis und optionale KI-Automatisierungen unterstützen.',
      },
      {
        question: 'Können Sie bestehende Anwendungen modernisieren?',
        answer:
          'Ja. Ich kann die aktuelle Architektur prüfen, Risiken identifizieren, Lieferung vereinfachen und gezielte Verbesserungen umsetzen.',
      },
      {
        question: 'Mit welchen Technologien arbeiten Sie?',
        answer:
          'Mein Kern-Stack umfasst Java, Spring Boot, React, Node.js, Kafka, Docker, Kubernetes, SQL, Git und CI/CD-Pipelines. Technologieentscheidungen treffe ich nach Business-Fit, nicht nach Trends allein.',
      },
    ],
  },
  contact: {
    h2: 'Erzählen Sie mir, was Sie bauen möchten.',
    intro:
      'Teilen Sie ein paar Details zu Ihrem Projekt, Ihrem Geschäftsziel oder Ihrer technischen Herausforderung. Ich melde mich mit dem nächsten praktischen Schritt.',
    directTitle: 'Direkter Kontakt',
    labels: {
      name: 'Name',
      email: 'E-Mail',
      phone: 'Telefonnummer',
      text: 'Erzählen Sie mir von Ihrem Projekt',
    },
    placeholders: {
      name: 'Max Mustermann',
      email: 'max@example.com',
      phone: '+41 77 000 00 00',
      text: 'Hallo Maxim, ich brauche Unterstützung beim Launch eines SaaS-MVP für...',
    },
    submit: 'Nachricht senden',
    sending: 'Wird gesendet...',
    success: 'Nachricht gesendet. Vielen Dank. Ich melde mich bald bei Ihnen.',
    failure:
      'Etwas ist schiefgelaufen. Bitte versuchen Sie es später erneut oder kontaktieren Sie mich direkt per E-Mail.',
    recaptchaDisclosure:
      'Dieses Formular ist durch Google reCAPTCHA geschützt. Details finden Sie in der Datenschutzerklärung.',
    validation: {
      name: 'Bitte geben Sie mindestens 2 Zeichen ein.',
      email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
      phone: 'Bitte geben Sie eine gültige Telefonnummer ein oder lassen Sie das Feld leer.',
      textMin: 'Bitte geben Sie mindestens 40 Zeichen ein.',
      textMax: 'Bitte halten Sie die Nachricht unter 5000 Zeichen.',
      token: 'Bitte schliessen Sie die reCAPTCHA-Prüfung ab.',
    },
  },
  footer: {
    summary:
      'Senior Software Engineer und KI-erfahrener Softwareberater für Schweizer Unternehmen, die digitale Produkte starten, automatisieren und skalieren möchten.',
    legalNotice: 'Impressum',
    dataProtection: 'Datenschutzerklärung',
    language: 'Sprache',
    copyright: 'Alle Rechte vorbehalten.',
  },
  common: {
    deliverables: 'Lieferumfang',
    includes: 'Enthält',
    email: 'E-Mail',
    phone: 'Telefon',
    externalLink: 'Öffnet eine externe Website',
  },
};
